import React, { Component } from "react";
import PropTypes from "prop-types";

import jsonLogic from "json-logic-js";

import moment from "moment";
import { connect } from "react-redux";

import { assign, cloneDeep, find, get, has, isEmpty, isEqual, map, merge, noop, } from "lodash";

import Grid from "@material-ui/core/Grid";
import Hidden from "@material-ui/core/Hidden";
import { withStyles } from "@material-ui/core/styles";

import updateOplog from "../../state/actions/updateOplog";
import assignAnswer from "../../state/actions/assignAnswer";
import assignContext from "../../state/actions/assignContext";
import removeUserError from "../../state/actions/removeUserError";

import { getDiscardedItems } from "../../utils/discarded";
import { generateContextVariables, isContextValid, resolveContextVariable } from "../../utils/context";
import {
  processGroups,
  processOptions,
} from "../../utils/import";
import { isQuestionAnswered } from "../../utils/required";
import randomize from "../../utils/randomize";
import getQuestionError from "../../state/selectors/userErrors";
import QuestionTitle from "./components/QuestionTitle";
import QuestionBody from "./components/QuestionBody";
import QuestionExtraText from "./components/QuestionExtraText";

import ControlWrapper from "../ControlWrapper";
import Input from "../Input";
import TextArea from "../TextArea";
import SearchableList from "../SearchableList";
import CheckboxList from "../CheckboxList";
import CheckboxGrid from "../CheckboxGrid";
import RadioList from "../RadioList";
import RadioGrid from "../RadioGrid";
import RatingList from "../RatingList";
import RatingGrid from "../RatingGrid";
// import Ranking from '../Ranking';
import ReadOnly from "../ReadOnly";

import { QUESTION_REQUIRED } from "../../state/userErrors";

const styles = () => ({
  root: {
    position: "relative",
    overflow: "hidden",
    height: "100%"
  }
});

/**
 * A question switch component which renders a question type specific component.
 */
class Question extends Component {
  constructor(props) {
    super(props);

    this.handleChange = this.handleChange.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.startTime = moment();

    this.state = {
      scrollOffset: 0
    };
  }

  handleScroll(offset) {
    this.setState({
      scrollOffset: offset
    });
  }

  handleChange(e, value) {
    const { onChange, name, distribution, duration: delta, } = this.props;
    const ctx = cloneDeep(get(this, ['state', 'payload'], {}));

    // Keep track of duration of each change
    const duration = delta + moment().diff(this.startTime);
    this.startTime = moment();

    // Pass the minimum fields needed
    const question = {
      name: this.props.name,
      type: this.props.type,
      requiredLogic: this.props.requiredLogic,
      contextVariable: this.props.contextVariable
    };

    // Re-create context from question properties
    const context = {
      groups: this.props.groups,
      options: this.props.options,
      discarded: this.props.discarded,
      custom: this.props.custom,
    };

    onChange({ name, value, question, context, distribution, duration, ctx });
  }

  renderUIControl() {
    switch (this.props.type) {
      case "CHECKBOX_LIST":
        return (
          <CheckboxList
            key={this.props.name}
            name={this.props.name}
            type={this.props.type}
            options={this.props.options}
            optionsRandomized={this.props.optionsRandomized}
            maxOptionsAllowed={this.props.maxOptionsAllowed}
            value={this.props.value}
            duration={this.props.duration}
            discarded={this.props.discarded}
            custom={this.props.custom}
            onInit={this.props.onInit}
            onChange={this.handleChange}
            doOplogUpdate={this.props.doOplogUpdate}
          />
        );

      case "CHECKBOX_GRID":
        return (
          <CheckboxGrid
            key={this.props.name}
            name={this.props.name}
            type={this.props.type}
            groups={this.props.groups}
            options={this.props.options}
            groupsRandomized={this.props.groupsRandomized}
            maxGroupsAllowed={this.props.maxGroupsAllowed}
            value={this.props.value}
            duration={this.props.duration}
            discarded={this.props.discarded}
            custom={this.props.custom}
            onInit={this.props.onInit}
            onChange={this.handleChange}
            doOplogUpdate={this.props.doOplogUpdate}
          />
        );

      case "RADIO_LIST":
        return (
          <RadioList
            key={this.props.name}
            name={this.props.name}
            type={this.props.type}
            options={this.props.options}
            optionsRandomized={this.props.optionsRandomized}
            value={this.props.value}
            duration={this.props.duration}
            discarded={this.props.discarded}
            custom={this.props.custom}
            onInit={this.props.onInit}
            onChange={this.handleChange}
            doOplogUpdate={this.props.doOplogUpdate}
          />
        );

      case "RADIO_GRID":
        return (
          <RadioGrid
            key={this.props.name}
            name={this.props.name}
            type={this.props.type}
            groups={this.props.groups}
            options={this.props.options}
            groupsRandomized={this.props.groupsRandomized}
            maxGroupsAllowed={this.props.maxGroupsAllowed}
            value={this.props.value}
            duration={this.props.duration}
            discarded={this.props.discarded}
            custom={this.props.custom}
            onInit={this.props.onInit}
            onChange={this.handleChange}
            doOplogUpdate={this.props.doOplogUpdate}
          />
        );

      case "RATING_LIST":
        return (
          <RatingList
            key={this.props.name}
            name={this.props.name}
            type={this.props.type}
            options={this.props.options}
            value={this.props.value}
            duration={this.props.duration}
            discarded={this.props.discarded}
            custom={this.props.custom}
            onInit={this.props.onInit}
            onChange={this.handleChange}
            doOplogUpdate={this.props.doOplogUpdate}
          />
        );

      case "RATING_GRID":
        return (
          <RatingGrid
            key={this.props.name}
            name={this.props.name}
            type={this.props.type}
            groups={this.props.groups}
            options={this.props.options}
            value={this.props.value}
            duration={this.props.duration}
            discarded={this.props.discarded}
            custom={this.props.custom}
            onInit={this.props.onInit}
            onChange={this.handleChange}
            doOplogUpdate={this.props.doOplogUpdate}
          />
        );

      case "SEARCHABLE_LIST":
        return (
          <SearchableList
            inputType="text"
            key={this.props.name}
            name={this.props.name}
            type={this.props.type}
            list={this.props.options}
            value={this.props.value}
            duration={this.props.duration}
            discarded={this.props.discarded}
            custom={this.props.custom}
            onInit={this.props.onInit}
            onChange={this.handleChange}
            onShowList={this.handleScroll}
            doOplogUpdate={this.props.doOplogUpdate}
          />
        );
      /*
      case 'RANKING':
        return (
          <Ranking
            key={this.props.name}
            options={this.props.options}
            maxOptionsAllowed={this.props.maxOptionsAllowed}
            value={this.props.value}
            onChange={this.handleChange}
          />
        );
      */
      case "TEXT":
        return (
          <ControlWrapper>
            <Input
              inputType="text"
              key={this.props.name}
              name={this.props.name}
              type={this.props.type}
              value={this.props.value.value}
              defaultValue={this.props.defaultValue}
              duration={this.props.duration}
              discarded={this.props.discarded}
              custom={this.props.custom}
              onInit={this.props.onInit}
              onChange={this.handleChange}
            />
          </ControlWrapper>
        );

      case "TEXTAREA":
        return (
          <TextArea
            key={this.props.name}
            name={this.props.name}
            type={this.props.type}
            maxLength={1024}
            value={this.props.value.value}
            duration={this.props.duration}
            discarded={this.props.discarded}
            custom={this.props.custom}
            onInit={this.props.onInit}
            onChange={this.handleChange}
            doOplogUpdate={this.props.doOplogUpdate}
          />
        );

      case "READONLY":
        return (
          <ReadOnly
            key={this.props.name}
            name={this.props.name}
            type={this.props.type}
            duration={this.props.duration}
            discarded={this.props.discarded}
            custom={this.props.custom}
            content={this.props.content}
            onInit={this.props.onInit}
            doOplogUpdate={this.props.doOplogUpdate}
          />
        );

      default:
        throw new Error(`Unknown question type: ${this.props.type}`);
    }
  }

  render() {
    const { classes } = this.props;

    return (
      <>
        <Grid item xs={12} className={classes.root}>
          {/* <Question imagesHost={this.props.imagesHost} /> */}
          <Hidden smDown>
            <QuestionTitle
              title={this.props.title}
              description={this.props.description}
              required={Boolean(this.props.requiredLogic)}
              error={this.props.error}
            />
            <QuestionBody>
              <Grid container>
                {!isEqual(this.props.type, "READONLY") &&
                  !isEmpty(this.props.content) && (
                    <Grid item xs={12}>
                      <QuestionExtraText content={this.props.content} />
                    </Grid>
                  )}
                <Grid item xs={12}>
                  {this.renderUIControl()}
                </Grid>
              </Grid>
            </QuestionBody>
          </Hidden>
          <Hidden mdUp>
            <QuestionBody
              error={this.props.error}
              offset={this.state.scrollOffset}
              mobile
            >
              <QuestionTitle
                title={this.props.title}
                description={this.props.description}
                required={Boolean(this.props.requiredLogic)}
                error={this.props.error}
                float
              />
              <Grid container>
                {!isEqual(this.props.type, "READONLY") &&
                  !isEmpty(this.props.content) && (
                    <Grid item xs={12}>
                      <QuestionExtraText content={this.props.content} />
                    </Grid>
                  )}
                <Grid item xs={12}>
                  {this.renderUIControl()}
                </Grid>
              </Grid>
            </QuestionBody>
          </Hidden>
        </Grid>
      </>
    );
  }
}

Question.propTypes = {
  // The name of the question
  name: PropTypes.string.isRequired, // The supported question types
  type: PropTypes.oneOf([
    "CHECKBOX_GRID",
    "CHECKBOX_LIST",
    "RADIO_GRID",
    "RADIO_LIST",
    "TEXT",
    "TEXTAREA",
    "SEARCHABLE_LIST",
    "RATING_LIST",
    "RATING_GRID",
    // "RANKING",
    "READONLY"
  ]).isRequired,
  // The user answer
  value: PropTypes.shape({
    value: PropTypes.string
  }),
  classes: PropTypes.shape().isRequired,
  // The default answer for the question
  defaultValue: PropTypes.oneOfType([PropTypes.any]),
  // The duration user spent at this question
  duration: PropTypes.number,
  // The title of the question as described in json
  title: PropTypes.string.isRequired,
  // The description of the question as described in json
  description: PropTypes.string,
  // The API error
  error: PropTypes.string,
  // The content of the question, a string with markdown
  content: PropTypes.string,
  // The options of the question
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.any,
      text: PropTypes.string,
      unique: PropTypes.bool,
      fixed: PropTypes.bool,
      writeIn: PropTypes.bool,
      exclusive: PropTypes.bool,
    })
  ),
  // Whether should shuffle the questions
  optionsRandomized: PropTypes.bool,
  // The count of allowed options
  maxOptionsAllowed: PropTypes.number,
  // The question groups
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      text: PropTypes.string,
      fixed: PropTypes.bool,
      writeIn: PropTypes.bool,
    })
  ),
  // Whether should shuffle the groups
  groupsRandomized: PropTypes.bool,
  // The count of allowed groups
  maxGroupsAllowed: PropTypes.number,
  // Whether the question is required
  requiredLogic: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  // A snapshot of the question at the time the user answered the questions
  contextVariable: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      input: PropTypes.array,
      // output: PropTypes.string,
    })
  ),
  // Distribution this question defines
  distribution: PropTypes.shape({
    input: PropTypes.array,
    limit: PropTypes.number,
  }),

  // Context variables defined by this question
  custom: PropTypes.object,
  // The excluded questions defined by this question's distribution
  discarded: PropTypes.array,

  // Callback to be invoked on mount
  onInit: PropTypes.func,
  // Callback to be invoked on change
  onChange: PropTypes.func,
  // Callback to be invoked on component dismount
  doOplogUpdate: PropTypes.func
};

Question.defaultProps = {
  value: {},
  defaultValue: null,
  duration: 0,
  description: null,
  error: null,
  content: "",
  options: [],
  optionsRandomized: false,
  maxOptionsAllowed: null,
  groups: [],
  groupsRandomized: false,
  maxGroupsAllowed: null,
  requiredLogic: false,
  contextVariable: null,
  distribution: { input: [], limit: 1 },
  custom: {},
  discarded: [],
  onInit: noop,
  onChange: noop
};

export { Question as Component };

function mapStateToProps(state) {
  const { cursor, questions } = state;

  // make a clone of the question object for further manipulation
  const question = cloneDeep(
    find(questions, { name: cursor.path[cursor.index] })
  );

  // get initial state of the component from context
  const initialContext = get(state, ["payload", "context", question.name]);
  // get answer from payload (if any)
  const initialAnswer = get(state, ["payload", "answer", question.name]);
  // get default answer (if any)
  const defaultValue = jsonLogic.apply(get(question, "defaultAnswer"), state);
  // get any current error for this question
  const error = getQuestionError(state.userErrors, question.name);

  // Interpolate any existing place holders based on the current context
  // TODO: this should unified to allow templating from metadata, etc.
  question.title = resolveContextVariable(question.title, state.payload.context);
  question.content = resolveContextVariable(question.content, state.meta);

  // Check if there's any distribution defined for the current question
  const distributionList = get(state, ["distributions"]);
  const distributionItem = find(distributionList, { name: question.name });

  const distributionInput = map(get(distributionItem, "input", []), item => {
    const question = find(questions, { name: item.name });
    return { ...item, displayLogic: get(question, "displayLogic", true ) };
  });
  const distributionLimit = get(distributionItem, "limit", 1);

  const distribution = { input: distributionInput, limit: distributionLimit };

  // If we have a valid state and answer then load these into the object
  if (
    !isEmpty(initialContext) &&
    isContextValid(
      question,
      state.payload.answer,
      state.payload.context
    )
  ) {
    // if (has(question, 'groups')
    question.groups = initialContext.groups; // loadGroups(initialState.groups);
    // if has(question, 'options')
    question.options = initialContext.options; // loadOptions(initialState.options);

    // TODO: add this below in the assign()?
    question.custom = initialContext.custom;
    question.discarded = initialContext.discarded;

    // Include answer in the initial state
    return assign({}, question, {
      distribution,
      value: initialAnswer.answer,
      duration: initialAnswer.duration,
      defaultValue,
      error
    });
  }

  // FIXME: (re)move this
  if (get(initialAnswer, "type") === "TEXTAREA") {
    question.value = initialAnswer.answer;
  }

  // Otherwise perform initial setup:
  question.groups = processGroups(question, { answer: state.payload.answer, context: state.payload.context }, { limit: true, unique: true });
  question.options = processOptions(question, { answer: state.payload.answer, context: state.payload.context }, { limit: true, unique: true });

  // Randomise groups and options if needed
  if (has(question, "groups") && question.groupsRandomized) {
    question.groups = randomize(question.groups, e => !e.fixed);
  }

  if (has(question, "options") && question.optionsRandomized) {
    question.options = randomize(question.options, e => !e.fixed);
  }

  // Generate initial set of discarded questions if applicable
  const discarded = getDiscardedItems(distribution, { answer: state.payload.answer, context: state.payload.context });
  question.discarded = discarded;

  return assign({}, question, { distribution, defaultValue, error });
}

function mapDispatchToProps(dispatch) {
  const onInit = (name, type, state, answer, duration) => {
    dispatch(assignContext(name, state));
    dispatch(assignAnswer(name, type, answer, duration));
  };

  const doOplogUpdate = oplog => {
    dispatch(updateOplog(oplog));
  };

  const onChange = ({ name, value, question, context, distribution, duration, ctx }) => {
    dispatch(assignAnswer(name, question.type, value, duration));

    // Generate up to date answer in order to update discarded items
    const localAnswer = {
      type: question.type,
      answer: value,
      duration: duration
    };

    merge(ctx, { "answer": { [name]: localAnswer } });
    merge(ctx, { "context": { [name]: context } });

    const discarded = getDiscardedItems(distribution, ctx);
    // TODO: consider passing full context to allow context variables making use of it
    const custom = question.contextVariable ? generateContextVariables(question, value, context) : {};

    // Pass context containing both fixed and custom properties
    dispatch(assignContext(name, assign({}, context, { custom, discarded })));

    // If question passes required logic, then reset error
    if (isQuestionAnswered(question, value, context)) {
      dispatch(removeUserError(QUESTION_REQUIRED, name));
    }
  };

  return { onInit, onChange, doOplogUpdate };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(Question));
